import dayjs from 'dayjs';
import { Bell, BellOff } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { PreviousReportsLinks } from '../components/PreviousReportsButton/PreviousReportsButton';
import { TextWithNAFallback } from '@/components/atoms';
import { markdownComponents } from '../components/markdown-components';
import { PreviousReportsButton } from '../components/PreviousReportsButton';
import { ReportWrapper } from '../components/ReportWrapper';

type RiskAnalysisSectionProps = {
  summary: string | undefined | null;
  ongoingMonitoringSummary: string | undefined | null;
  homepageScreenshotUrl?: string | null;
  gatedContentScreenshotUrl?: string | null;
  createdAt: Date;
  previousReportsLinks?: PreviousReportsLinks | null;
  isAlert?: boolean;
  isMonitoringReport?: boolean;
};

export const RiskAnalysisSection = ({
  summary,
  ongoingMonitoringSummary,
  homepageScreenshotUrl,
  gatedContentScreenshotUrl,
  createdAt,
  previousReportsLinks,
  isAlert = false,
  isMonitoringReport = false,
}: RiskAnalysisSectionProps) => {
  const screenshotSrc = gatedContentScreenshotUrl || homepageScreenshotUrl;
  const screenshotAlt = gatedContentScreenshotUrl
    ? 'Gated Content Screenshot'
    : 'Homepage Screenshot';

  return (
    <>
      {ongoingMonitoringSummary && (
        <ReportWrapper
          title={
            <div className="flex items-center gap-3">
              <span>{`Ongoing Monitoring Summary - ${dayjs(createdAt).format('MMM, YYYY')}`}</span>
              {isMonitoringReport && (
                <div className="flex items-center gap-2 rounded-md bg-gray-50 px-2.5 py-1 text-sm font-medium">
                  {isAlert ? (
                    <>
                      <div className="relative">
                        <Bell className="h-4 w-4 text-gray-900" />
                        <div className="absolute right-[1.5px] top-[1.5px] h-[6px] w-[6px] rounded-full border border-white bg-red-500" />
                      </div>
                      <span className="text-sm text-gray-900">Changes Detected</span>
                    </>
                  ) : (
                    <>
                      <BellOff className="h-4 w-4 text-neutral-900" />
                      <span className="text-sm text-neutral-800">No Changes</span>
                    </>
                  )}
                </div>
              )}
            </div>
          }
          headerAction={
            previousReportsLinks ? (
              <PreviousReportsButton previousReportsLinks={previousReportsLinks} />
            ) : undefined
          }
          showTooltip={false}
        >
          <ReactMarkdown components={markdownComponents}>{ongoingMonitoringSummary}</ReactMarkdown>
        </ReportWrapper>
      )}
      <ReportWrapper title="Risk Analysis" showTooltip={false}>
        <TextWithNAFallback
          as="div"
          className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
        >
          {summary && (
            <div className="space-y-3 leading-relaxed">
              {screenshotSrc && (
                <div className="float-right mb-4 ml-6 w-1/3">
                  <div className="block overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
                    <div className="relative aspect-[4/3] bg-gray-100">
                      <img
                        src={screenshotSrc}
                        alt={screenshotAlt}
                        className="h-full w-full object-cover object-top"
                        onError={e => {
                          const target = e.target as HTMLImageElement;
                          const container = target.parentElement?.parentElement?.parentElement;

                          if (container) {
                            container.style.display = 'none';
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {summary}
              </ReactMarkdown>
              {screenshotSrc && <div className="clear-both" />}
            </div>
          )}
        </TextWithNAFallback>
      </ReportWrapper>
    </>
  );
};
